import type React from 'react';
import { useMemo } from 'react';

import { cn } from '@change/ds-utils';

import { useSandbox } from './hooks/useSandbox';
import { useSandboxReadiness } from './hooks/useSandboxReadiness';
import { useWindowMessage } from './hooks/useWindowMessage';
import type { SandboxContract } from './types';

type SubmitAction = SandboxContract['actions']['submit'];
type SubmitParams = Parameters<SubmitAction>[0];
type ErrorParams = Parameters<SandboxContract['actions']['error']>[0];

type SandboxIframeProps = {
	src: string;
	/** a11y — the iframe needs an accessible name. */
	title: string;
	/**
	 * Overrides how the child's `submit` message is handled. Receives the raw params
	 * plus the real `submit` action, and decides if/when to call it — e.g. to gate on
	 * an async step (a mutation) before committing to the state machine. When omitted,
	 * `submit` is called directly, same as every other sandbox action.
	 */
	onSubmit?: (params: SubmitParams, submit: SubmitAction) => void | Promise<void>;
	/**
	 * Side effect for a child-reported `error` message. The channel validates and reports
	 * the error regardless; this decides what else happens (e.g. switch on `code`). When
	 * omitted, a child error is reported only.
	 */
	onError?: (params: ErrorParams) => void;
	/**
	 * Side effect for a load timeout — the child never handshaked `ready` in time. The channel
	 * reports it regardless; this decides what else happens. When omitted, a timeout is
	 * reported only.
	 */
	onLoadTimeout?: () => void;
	/**
	 * Hides the iframe (native `hidden` attribute) without unmounting it, so its document
	 * — and any in-memory state the child holds, e.g. conversation history — survives
	 * whatever's being shown in front of it (a loading state, an overlay, etc.).
	 */
	hidden?: boolean;
};

/**
 * iframe sandbox boundary and container. Wires the fixed sandbox actions to the
 * state machine and renders the iframe; the iframe (a separate document) reaches
 * page state only through those messages. Initial data is baked into `src` by
 * whatever renders this.
 *
 * The child origin messages are trusted from is derived from `src` itself, so
 * whatever renders this is the single source of truth for which child it embeds.
 */
export function SandboxIframe({
	src,
	title,
	onSubmit,
	onError,
	onLoadTimeout,
	hidden,
}: SandboxIframeProps): React.ReactElement {
	const { actions } = useSandbox({ onSubmit, onError });
	const childOrigin = useMemo(() => new URL(src).origin, [src]);

	useWindowMessage('submit', childOrigin, actions.submit);

	useWindowMessage('back', childOrigin, actions.back);

	useWindowMessage('update', childOrigin, actions.update);

	useWindowMessage('track', childOrigin, actions.track);

	useWindowMessage('error', childOrigin, actions.error);

	const { status } = useSandboxReadiness({ childOrigin, onLoadTimeout });
	// Before `ready`, the child must still load and postMessage while visually hidden — but
	// native `hidden` (display:none) can prevent exactly that: Safari never attaches a
	// display:none iframe to the DOM on initial render, and Chrome render-throttles
	// cross-origin display:none iframes out of rAF/ResizeObserver, either of which can starve
	// the child's boot sequence before it ever posts `ready`. So pre-ready hiding stays visual
	// (opacity) rather than removing the iframe from rendering.
	const hideUntilReady = status !== 'ready';
	// The caller's `hidden` prop only ever applies natively once `ready`
	const isNativelyHidden = status === 'ready' && hidden;

	return (
		<iframe
			className={cn('h-[calc(100vh-60px)] w-full', hideUntilReady && 'pointer-events-none opacity-0')}
			src={src}
			title={title}
			sandbox="allow-forms allow-scripts allow-same-origin"
			referrerPolicy="strict-origin-when-cross-origin"
			aria-hidden={hideUntilReady || undefined}
			tabIndex={hideUntilReady ? -1 : undefined}
			hidden={isNativelyHidden}
		/>
	);
}
